import { useEffect, useRef, useMemo, useCallback } from 'react'
import type { GraphData } from '../types'

interface UseBrainSimulationProps {
  data: GraphData
  edgeTimeMs: number
  fadeMs: number
  isRunning: boolean
  startNodeId?: string
}

export interface BrainState {
  nodes: Map<string, number>
  links: Map<string, number>
}

interface PendingArrival {
  nodeId: string
  parentIds: Set<string>
  arriveAt: number
}

interface WaitingEntry {
  nodeId: string
  parentIds: Set<string>
}

interface UseBrainSimulationResult {
  stateRef: React.MutableRefObject<BrainState>
  nodeCount: number
  reset: () => void
}

export function useBrainSimulation({
  data,
  edgeTimeMs,
  fadeMs,
  isRunning,
  startNodeId,
}: UseBrainSimulationProps): UseBrainSimulationResult {
  const stateRef = useRef<BrainState>({
    nodes: new Map(),
    links: new Map(),
  })

  const pendingRef = useRef<PendingArrival[]>([])
  const waitingRef = useRef<WaitingEntry[]>([])
  const lastLitRef = useRef<Map<string, number>>(new Map())

  // Refractory derived from edge time (NOT fade) — 3 hops worth of cooldown
  const refractoryMs = edgeTimeMs * 3

  const adjacency = useMemo(() => {
    const map = new Map<string, Array<{ neighborId: string; linkKey: string }>>()
    for (const link of data.links) {
      const srcId = typeof link.source === 'object' ? (link.source as any).id : link.source
      const tgtId = typeof link.target === 'object' ? (link.target as any).id : link.target
      const linkKey = `${srcId}|${tgtId}|${link.relationship}`
      if (!map.has(srcId)) map.set(srcId, [])
      map.get(srcId)!.push({ neighborId: tgtId, linkKey })
      if (!map.has(tgtId)) map.set(tgtId, [])
      map.get(tgtId)!.push({ neighborId: srcId, linkKey })
    }
    return map
  }, [data.links])

  const reset = useCallback(() => {
    stateRef.current = { nodes: new Map(), links: new Map() }
    pendingRef.current = []
    waitingRef.current = []
    lastLitRef.current = new Map()
  }, [])

  useEffect(() => {
    if (!isRunning) return

    let rafId: number
    let lastFrame = performance.now()
    let started = false
    const originId = startNodeId || data.nodes[0]?.id

    function fireNode(nodeId: string, parentIds: Set<string>, now: number, s: BrainState) {
      const edges = adjacency.get(nodeId)
      if (!edges) return

      let propagated = false
      for (const { neighborId, linkKey } of edges) {
        if (parentIds.has(neighborId)) continue
        const nLastLit = lastLitRef.current.get(neighborId) ?? -Infinity
        if ((now - nLastLit) >= refractoryMs) {
          s.links.set(linkKey, 1.0)
          addPending(neighborId, new Set([nodeId]), now + edgeTimeMs)
          propagated = true
        }
      }

      if (!propagated) {
        waitingRef.current.push({ nodeId, parentIds })
      }
    }

    function addPending(nodeId: string, parentIds: Set<string>, arriveAt: number) {
      const existing = pendingRef.current.find(
        p => p.nodeId === nodeId && Math.abs(p.arriveAt - arriveAt) < edgeTimeMs * 0.5
      )
      if (existing) {
        for (const p of parentIds) existing.parentIds.add(p)
      } else {
        pendingRef.current.push({ nodeId, parentIds, arriveAt })
      }
    }

    const loop = (now: number) => {
      rafId = requestAnimationFrame(loop)
      const dt = now - lastFrame
      lastFrame = now
      if (dt <= 0) return

      const s = stateRef.current

      if (!started && originId) {
        started = true
        s.nodes.set(originId, 1.0)
        lastLitRef.current.set(originId, now)
        fireNode(originId, new Set(), now, s)
      }

      // Decay brightness (time-based, only visual — doesn't affect propagation)
      const decay = dt / fadeMs
      for (const [id, val] of s.nodes) {
        const next = val - decay
        if (next <= 0) s.nodes.delete(id)
        else s.nodes.set(id, next)
      }
      for (const [key, val] of s.links) {
        const next = val - decay
        if (next <= 0) s.links.delete(key)
        else s.links.set(key, next)
      }

      // Check pending arrivals
      const arrived = new Map<string, Set<string>>()
      const stillPending: PendingArrival[] = []
      for (const pa of pendingRef.current) {
        if (now >= pa.arriveAt) {
          if (arrived.has(pa.nodeId)) {
            for (const p of pa.parentIds) arrived.get(pa.nodeId)!.add(p)
          } else {
            arrived.set(pa.nodeId, new Set(pa.parentIds))
          }
        } else {
          stillPending.push(pa)
        }
      }
      pendingRef.current = stillPending

      // Fire arrived nodes
      for (const [nodeId, parentIds] of arrived) {
        s.nodes.set(nodeId, 1.0)
        lastLitRef.current.set(nodeId, now)
        fireNode(nodeId, parentIds, now, s)
      }

      // Process waiting queue — bounce when refractory clears
      const newWaiting: WaitingEntry[] = []
      for (const entry of waitingRef.current) {
        const edges = adjacency.get(entry.nodeId)
        if (!edges) continue
        let bounced = false
        for (const { neighborId, linkKey } of edges) {
          const nLastLit = lastLitRef.current.get(neighborId) ?? -Infinity
          if ((now - nLastLit) >= refractoryMs) {
            s.links.set(linkKey, 1.0)
            addPending(neighborId, new Set([entry.nodeId]), now + edgeTimeMs)
            bounced = true
          }
        }
        if (!bounced) newWaiting.push(entry)
      }
      waitingRef.current = newWaiting

      // Re-seed if everything is dead
      if (pendingRef.current.length === 0 && waitingRef.current.length === 0 && originId) {
        const oLastLit = lastLitRef.current.get(originId) ?? -Infinity
        if ((now - oLastLit) >= refractoryMs) {
          s.nodes.set(originId, 1.0)
          lastLitRef.current.set(originId, now)
          fireNode(originId, new Set(), now, s)
        }
      }
    }

    rafId = requestAnimationFrame(loop)
    return () => cancelAnimationFrame(rafId)
  }, [isRunning, edgeTimeMs, fadeMs, adjacency, startNodeId, data.nodes, refractoryMs])

  return { stateRef, nodeCount: data.nodes.length, reset }
}
